import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminMe, useAdminLogout, useAdminNotifications } from "../hooks/useApi";

const DISMISSED_KEY = "admin_dismissed_notifications";

function loadDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return new Set(arr as string[]);
    return new Set();
  } catch {
    return new Set();
  }
}
function saveDismissed(ids: Set<string>) {
  localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids]));
}

export default function AdminHeader() {
  const { data: user } = useAdminMe();
  const logout = useAdminLogout();
  const { data: notifications } = useAdminNotifications();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(() => loadDismissed());

  // Keep dismissed in sync across tabs / page navigations
  useEffect(() => {
    const onStorage = () => setDismissed(loadDismissed());
    window.addEventListener("storage", onStorage);
    const onFocus = () => setDismissed(loadDismissed());
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // Sync dismissed when notifications change (prune stale ids is handled in page, just refresh view here)
  useEffect(() => {
    setDismissed(loadDismissed());
  }, [notifications?.items?.length]);

  const visibleItems = useMemo(() => {
    const items = (notifications?.items as { id: string }[] | undefined) ?? [];
    return (notifications?.items as { id: string; type: string; title: string; message: string; link: string }[] | undefined)?.filter(
      (n) => !dismissed.has(n.id)
    ) ?? items as never;
  }, [notifications, dismissed]);

  const unreadCount = visibleItems.length;

  const dismissOne = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    saveDismissed(next);
  };

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const roleLabel = user?.is_admin
    ? "مدیر سیستم"
    : (user?.roles || []).map((r: { display_name: string }) => r.display_name).join("، ") || "کاربر سیستم";

  const initials = (user?.name || user?.email || "A")
    .charAt(0)
    .toUpperCase();

  return (
    <header className="bg-white px-6 py-3 sticky top-0 z-30" style={{ borderBottom: "1px solid var(--admin-border)" }}>
      <div className="flex items-center justify-between gap-4">
        {/* ─── Right: Search ─── */}
        <div className="flex-1 max-w-md">
          {searchOpen ? (
            <div className="relative">
              <input
                type="text"
                className="admin-input !h-10 !pr-10 !rounded-xl text-sm"
                placeholder="جستجو در پنل مدیریت..."
                autoFocus
                onBlur={() => setSearchOpen(false)}
              />
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: "var(--admin-text-tertiary)" }}
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
          ) : (
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm w-full transition-all"
              style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)", color: "var(--admin-text-tertiary)" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(7,135,131,0.3)"; e.currentTarget.style.color = "var(--admin-text-secondary)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--admin-border)"; e.currentTarget.style.color = "var(--admin-text-tertiary)"; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <span>جستجو...</span>
            </button>
          )}
        </div>

        {/* ─── Left: Actions ─── */}
        <div className="flex items-center gap-3">
          {/* Notification Bell */}
          <div className="relative" ref={bellRef}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative w-10 h-10 rounded-xl flex items-center justify-center transition-all"
              style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)", color: "var(--admin-text-secondary)" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(7,135,131,0.3)"; e.currentTarget.style.color = "var(--admin-primary)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--admin-border)"; e.currentTarget.style.color = "var(--admin-text-secondary)"; }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 01-3.46 0" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-[#DC2626] text-white text-[9px] flex items-center justify-center font-bold">
                  {unreadCount}
                </span>
              )}
            </button>

            {/* Notifications Panel */}
            {showNotifications && (
              <div className="absolute left-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] admin-glass rounded-2xl overflow-hidden z-50">
                <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: "var(--admin-border)" }}>
                  <p className="text-sm font-semibold" style={{ color: "var(--admin-text-primary)" }}>اعلان‌ها</p>
                  <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                    {unreadCount > 0 ? `${unreadCount} مورد نیازمند توجه` : "همه‌چیز مرتب است"}
                  </span>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {visibleItems.length ? (
                    visibleItems.map((n: { id: string; type: string; title: string; message: string; link: string }) => (
                      <div
                        key={n.id}
                        className="w-full text-right px-3 py-3 flex items-start gap-3 transition-colors hover:bg-[var(--admin-bg-hover)] group"
                        style={{ borderBottom: "1px solid var(--admin-border-light)" }}
                      >
                        <button
                          onClick={() => {
                            setShowNotifications(false);
                            navigate(n.link);
                          }}
                          className="flex items-start gap-3 flex-1 min-w-0 text-right"
                        >
                          <span
                            className="mt-0.5 w-8 h-8 shrink-0 rounded-full flex items-center justify-center"
                            style={{
                              background:
                                n.type === "request"
                                  ? "var(--admin-error-bg)"
                                  : n.type === "payment"
                                  ? "var(--admin-warning-bg)"
                                  : "var(--admin-info-bg)",
                              color:
                                n.type === "request"
                                  ? "var(--admin-error)"
                                  : n.type === "payment"
                                  ? "var(--admin-warning)"
                                  : "var(--admin-primary)",
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                              {n.type === "request" ? (
                                <><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 01-3.46 0" /></>
                              ) : n.type === "payment" ? (
                                <path d="M3 3h18v18H3zM3 9h18" />
                              ) : (
                                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                              )}
                            </svg>
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-medium truncate" style={{ color: "var(--admin-text-primary)" }}>{n.title}</span>
                            <span className="block text-xs mt-0.5 line-clamp-2" style={{ color: "var(--admin-text-tertiary)" }}>{n.message}</span>
                          </span>
                        </button>
                        <button
                          onClick={(e) => dismissOne(n.id, e)}
                          className="shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                          style={{ borderColor: "var(--admin-border)", color: "var(--admin-text-secondary)", background: "#fff" }}
                          title="خوانده شده"
                        >
                          خوانده شده
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-10 text-center">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="mx-auto mb-3" style={{ color: "var(--admin-text-tertiary)" }}>
                        <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                        <path d="M13.73 21a2 2 0 01-3.46 0" />
                      </svg>
                      <p className="text-sm" style={{ color: "var(--admin-text-tertiary)" }}>اعلانی وجود ندارد</p>
                    </div>
                  )}
                </div>
                <div className="p-2 border-t flex gap-2" style={{ borderColor: "var(--admin-border)" }}>
                  <button
                    onClick={() => {
                      setShowNotifications(false);
                      navigate("/admin/notifications");
                    }}
                    className="flex-1 text-center text-sm py-2 rounded-xl transition-colors font-medium"
                    style={{ color: "var(--admin-primary)", background: "var(--admin-primary-light)" }}
                  >
                    مشاهده همه
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* User Profile Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-2.5 px-3 py-2 rounded-xl transition-all"
              style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}
            >
              <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-sm font-semibold" style={{ background: "var(--admin-primary)" }}>
                {initials}
              </div>
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium leading-tight" style={{ color: "var(--admin-text-primary)" }}>
                  {user?.name || user?.email || "مدیر"}
                </p>
                <p className="text-[11px]" style={{ color: "var(--admin-text-tertiary)" }}>{roleLabel}</p>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ color: "var(--admin-text-tertiary)" }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {/* Dropdown */}
            {showDropdown && (
              <div className="absolute left-0 top-full mt-2 w-56 admin-glass rounded-2xl overflow-hidden z-50">
                <div className="p-3 border-b" style={{ borderColor: "var(--admin-border)" }}>
                  <p className="text-sm font-medium" style={{ color: "var(--admin-text-primary)" }}>{user?.name || user?.email}</p>
                  <p className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>{user?.email}</p>
                </div>
                <div className="p-1">
                  <a
                    href="/admin/settings"
                    className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm transition-colors"
                    style={{ color: "var(--admin-text-secondary)" }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
                    </svg>
                    تنظیمات پروفایل
                  </a>
                  <a
                    href="/dashboard"
                    className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm transition-colors"
                    style={{ color: "var(--admin-text-secondary)" }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    پنل کاربری
                  </a>
                </div>
                <div className="p-1 border-t" style={{ borderColor: "var(--admin-border)" }}>
                  <button
                    onClick={() => {
                      setShowDropdown(false);
                      logout.mutate();
                    }}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm transition-colors w-full"
                    style={{ color: "var(--admin-error)" }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    خروج
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}